import type { ReactNode } from 'react'
import { formatMoney } from '../../lib/utils'
import { diaMes } from '../../lib/corte'
import type { DivergenciaAbertura, PreviaConfiguracaoInfo, PreviaSaldoAbertura } from '../../types'

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-13">
      <span className="text-muted">{rotulo}</span>
      <b className="text-right text-white">{children}</b>
    </div>
  )
}

const sinal = (v: number) => `${v >= 0 ? '+ ' : '- '}${formatMoney(Math.abs(v))}`

export function Divergencias({ lista }: { lista: DivergenciaAbertura[] }) {
  if (!lista.length) return null
  return (
    <ul className="space-y-1 rounded border border-warning/40 bg-warning/10 p-3 text-13 text-warning">
      {lista.map((d, i) => (
        <li key={`${d.codigo}-${i}`} className="flex gap-2">
          <i className="ri-error-warning-line mt-0.5" aria-hidden />
          <span>
            {d.mensagem}
            {d.quantidade > 0 && ` (${d.quantidade})`}
            {d.valor != null && ` · ${formatMoney(d.valor)}`}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Quadro "Como o saldo vai ficar" do assistente de corte de um vínculo Pluggy. Os números vêm da prévia do servidor, que lê
 * as transações do banco desde o corte e faz o mesmo que a importação (sem gravar nada).
 */
export function PreviaPluggy({ previa, calculando, erro, dataCorte, saldoBanco, ehCartao }: {
  previa?: PreviaConfiguracaoInfo
  calculando: boolean
  erro?: string | null
  dataCorte: string
  saldoBanco?: number | null
  ehCartao: boolean
}) {
  const curta = diaMes(dataCorte)
  const titulo = ehCartao ? 'O que vai entrar' : 'Como o saldo vai ficar'
  const cor = ehCartao ? 'border-info/50' : 'border-income/60'

  if (!previa) {
    return (
      <div className={`rounded border ${cor} bg-surface2/40 p-4 text-13`} role="status">
        <p className={`font-semibold ${ehCartao ? 'text-info' : 'text-income'}`}>{titulo}</p>
        <p className="mt-2 text-muted">
          {calculando ? 'Calculando... estou conferindo os lançamentos do banco desde essa data.' : erro ? `Não foi possível calcular agora: ${erro}` : 'Informe a data para ver o que vai entrar.'}
        </p>
      </div>
    )
  }

  const igual = saldoBanco != null && previa.saldoFinal != null && Math.abs(previa.saldoFinal - saldoBanco) < 0.005
  const compras = previa.pendenciasNova + previa.aceitasAutomaticamente
  const outras = Math.max(0, previa.pendencias - previa.pendenciasNova)
  return (
    <div className={`space-y-2 rounded border ${cor} bg-surface2/40 p-4 transition-opacity ${calculando ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <p className={`text-13 font-semibold ${ehCartao ? 'text-info' : 'text-income'}`}>{titulo}</p>
        <span className="flex gap-2">
          {calculando && <span className="rounded-full bg-info/15 px-2 py-0.5 text-2xs font-medium text-info" role="status">calculando...</span>}
          {previa.truncado && <span className="rounded-full bg-warning/15 px-2 py-0.5 text-2xs font-medium text-warning">estimativa</span>}
        </span>
      </div>
      {ehCartao ? (
        <>
          <Linha rotulo="Compras a importar">{compras}{previa.truncado && '+'}</Linha>
          {outras > 0 && <Linha rotulo="Estornos e pagamentos de fatura (você decide na caixa de entrada)">{outras}</Linha>}
        </>
      ) : (
        <>
          <Linha rotulo="Lançamentos a importar">{previa.lancamentosEncontrados}{previa.truncado && '+'}</Linha>
          {previa.efeitoLancamentos != null && <Linha rotulo="Efeito deles no saldo">{sinal(previa.efeitoLancamentos)}</Linha>}
          {previa.saldoAbertura != null && <Linha rotulo={`Saldo inicial em ${curta}`}>{formatMoney(previa.saldoAbertura)}</Linha>}
          {previa.saldoFinal != null && (
            <Linha rotulo="Saldo final">
              <span className="text-income">{formatMoney(previa.saldoFinal)}{igual && ' (igual ao banco)'}</span>
            </Linha>
          )}
        </>
      )}
      <p className="text-2xs text-muted">
        {ehCartao ? '' : 'Sem lançamento de ajuste no extrato. '}O que veio antes de {curta} não é importado{previa.historicoIgnorado > 0 ? ` (${previa.historicoLimitado ? 'mais de ' : ''}${previa.historicoIgnorado} no banco)` : ''}.
        {previa.truncado && ' O banco tem mais lançamentos do que a leitura alcança de uma vez: os números são uma estimativa.'}
      </p>
    </div>
  )
}

/** Prévia da conta manual / refazer: saldo atual x novo e o que vira histórico. */
export function PreviaManual({ previa }: { previa: PreviaSaldoAbertura }) {
  return (
    <div className="space-y-2 rounded border border-info/50 bg-surface2/40 p-4">
      <p className="text-13 font-semibold text-info">O que vai acontecer</p>
      <Linha rotulo="Saldo atual">{formatMoney(previa.saldoAtual)}</Linha>
      <Linha rotulo="Saldo novo">{formatMoney(previa.saldoNovo)}</Linha>
      <Linha rotulo={`Saldo inicial em ${diaMes(previa.dataCorte)}`}>{formatMoney(previa.saldoAbertura)}</Linha>
      <Linha rotulo="Lançamentos que viram histórico">{previa.lancamentosHistorico}</Linha>
      <Linha rotulo="Lançamentos que continuam no saldo">{previa.lancamentosQueContam}</Linha>
    </div>
  )
}
